/**
 * Screen-2.tsx
 * Last updated: 2025-08-12 15:47:54
 * Updated by: JulianPolly
 */

import React, { useEffect, useState } from "react";
import { ScreenProps } from "../../SequenceController";
import { Icon } from "@blueprintjs/core";
import Streamnit from "./assets/Streamit.png";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles } from "../../../../components/colors";

type ScreenClick = {
    choice: number;
    millisecondsSinceFirstRender: number;
    bedingung: string;
};

type ScreenResult = {
    selections: Array<ScreenClick>,
    usedHelp: boolean
};

export const Screen2Component = (props: ScreenProps) => {
    const [firstRenderTimestamp, setFirstRenderTimestamp] = useState(0);
    const { setCanContinue, result, setResult } = props;
    const { t } = useLanguage();

    useEffect(() => {
        setFirstRenderTimestamp(Date.now());
    }, [setResult]);

    const selected = (result.selections.length > 0) ? result.selections[result.selections.length - 1].choice : -1;

    // Button nur aktivieren, wenn Auswahl getroffen wurde
    useEffect(() => {
        setCanContinue(selected !== -1);
    }, [selected, setCanContinue]);

    const addSelection = (choice: number) => {
        setResult((result: ScreenResult) => {
            result.selections.push({
                choice,
                millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp,
                bedingung: "Bedingung 2"
            });
            return result;
        });
    };

    // Formatieren der Inhalte, um HTML-Entitäten korrekt anzuzeigen
    const formatContent = (htmlContent: string) => {
        // Ersetzen von &nbsp; mit einem geschützten Leerzeichen im HTML
        return htmlContent.replace(/&nbsp;/g, ' ');
    };

    const contentHtml = formatContent(t('experiments', 'mentalAccounting', 'screens', 'screen2', 'content'));
    const questionHtml = formatContent(t('experiments', 'mentalAccounting', 'screens', 'screen2', 'question'));

    return (
        <div className="experiment-container" style={{
            maxWidth: 1250,
            margin: "0 auto",
            padding: "24px 20px 44px",
            backgroundColor: colors.backgroundLight
        }}>
            {/* Main content card */}
            <div style={{
                background: colors.white,
                borderRadius: 14,
                boxShadow: "0 2px 12px rgba(0,0,0,0.09)",
                padding: "24px 40px 36px",
                marginBottom: 28
            }}>
                {/* Heading */}
                <h2 style={{
                    color: colors.brillantBlue,
                    margin: "0 0 24px 0",
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: "center"
                }}>
                    {t('experiments', 'mentalAccounting', 'screens', 'screen2', 'title')}
                </h2>

                {/* Content with better spacing and typography */}
                <div style={{
                    fontSize: 17,
                    lineHeight: 1.7,
                    color: colors.textMedium,
                    marginBottom: 30,
                    textAlign: "justify",
                    textJustify: "inter-word"
                }}
                     dangerouslySetInnerHTML={{ __html: contentHtml }}
                />

                {/* Question header */}
                <h3 style={{
                    color: colors.brillantBlue,
                    textAlign: "center",
                    marginBottom: 30,
                    fontSize: 24,
                    fontWeight: 600
                }}
                    dangerouslySetInnerHTML={{ __html: questionHtml }}
                />

                {/* Options with improved layout */}
                <div style={{
                    display: "flex",
                    gap: 24,
                    flexDirection: window.innerWidth < 600 ? "column" : "row",
                    justifyContent: "center"
                }}>
                    {/* Yes option */}
                    <div
                        style={selected === 1
                            ? commonStyles.optionCard.selected
                            : commonStyles.optionCard.default}
                        onClick={() => addSelection(1)}
                    >
                        {selected === 1 && (
                            <div style={commonStyles.selectionCheck}>
                                <Icon icon="tick" size={16} />
                            </div>
                        )}

                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            marginBottom: 20
                        }}>
                            <img
                                src={Streamnit}
                                alt="StreamIt"
                                style={{
                                    maxWidth: "80%",
                                    maxHeight: 120,
                                    objectFit: "contain"
                                }}
                            />
                        </div>

                        <div style={{
                            textAlign: "center",
                            fontSize: 18,
                            fontWeight: 500,
                            color: colors.textDark,
                            padding: "0 16px"
                        }}
                             dangerouslySetInnerHTML={{
                                 __html: formatContent(t('experiments', 'mentalAccounting', 'screens', 'screen2', 'options', 'yes'))
                             }}
                        />
                    </div>

                    {/* No option */}
                    <div
                        style={selected === 2
                            ? commonStyles.optionCard.selected
                            : commonStyles.optionCard.default}
                        onClick={() => addSelection(2)}
                    >
                        {selected === 2 && (
                            <div style={commonStyles.selectionCheck}>
                                <Icon icon="tick" size={16} />
                            </div>
                        )}

                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            marginBottom: 20
                        }}>
                            <img
                                src={Streamnit}
                                alt="StreamIt"
                                style={{
                                    maxWidth: "80%",
                                    maxHeight: 120,
                                    objectFit: "contain"
                                }}
                            />
                        </div>

                        <div style={{
                            textAlign: "center",
                            fontSize: 18,
                            fontWeight: 500,
                            color: colors.textDark,
                            padding: "0 16px"
                        }}
                             dangerouslySetInnerHTML={{
                                 __html: formatContent(t('experiments', 'mentalAccounting', 'screens', 'screen2', 'options', 'no'))
                             }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export const Screen2: ScreenConfig = {
    defaultResult: {
        selections: [],
        usedHelp: false
    },
    canContinueDefault: true,
    component: Screen2Component
};